import { z } from 'zod';
import { hhmm, isoDate, pgTime, role, timestamptz, uuid } from './common';
import { userUpsertForm } from './forms';
import { taskPriority } from './rows';
import { unifiClientRecordSchema } from './unifi';
import { WA_TEXT_MAX } from './wa';

/**
 * Request / response contracts of apps/api (ARCHITECTURE.md §6). Every body the API accepts is parsed
 * with one of these schemas; every JSON it returns satisfies the matching response schema. This module
 * holds declarations only — behaviour (window check, presence slice, push payload) lives in apps/api.
 */

// ---- error envelope ---------------------------------------------------------------------------

export const API_ERROR_CODES = [
  'validation',
  'unauthorized',
  'forbidden',
  'not_found',
  'setup_done',
  'username_taken',
  'employee_taken',
  'no_employees',
  'rate_limited',
  'bad_request',
  'whatsapp_failed',
  'unifi_failed',
  'invoice_reader_off',
  'invoice_read_failed',
  'internal_error',
] as const;
export type ApiErrorCode = (typeof API_ERROR_CODES)[number];

/** `{ error, message }` — `message` is the toast text the UI shows verbatim. */
export const apiErrorSchema = z.object({ error: z.enum(API_ERROR_CODES), message: z.string() });
export type ApiError = z.infer<typeof apiErrorSchema>;

/** Texts the API answers with. Prototype strings are marked; the rest are new (reported in the phase notes). */
export const API_MESSAGES = {
  /** Prototype (RLS / RPC access guard). */
  noAccess: 'Nuk ke qasje në këtë pjesë',
  /** Prototype line 3125 (`saveAuthUser`). */
  usernameTaken: 'Ky username ekziston',
  /** Prototype attendance section. */
  employeeNotFound: 'Punëtori nuk u gjet',
  /** Prototype line 3018 (`simulateWifiPresence`). */
  noEmployees: 'Së pari regjistro të paktën një punëtor',
  /** Prototype line 3018 (`simulateWifiPresence`). */
  simulationDone: 'U simulua lidhja Wi‑Fi për testim',
  /** Prototype line 3018 (`simulateWifiPresence`). */
  simulationState: 'Simulim Wi‑Fi aktiv',
  /** Prototype line 3017 (`syncUnifiAttendance`). */
  unifiOffline: 'UniFi nuk është lidhur',
  /** Prototype line 2807 (`sendDebtWhatsapp`). */
  noPhone: 'Klienti nuk ka numër telefoni të regjistruar',
  /** Prototype WhatsApp page empty state. */
  conversationNotFound: 'Nuk u gjet asnjë bisedë.',
  /** Prototype WhatsApp reply box placeholder, reused for an empty reply body. */
  messageRequired: 'Shkruaj përgjigjen...',
  /** Prototype WhatsApp page (no conversation selected). */
  conversationRequired: 'Zgjedh një bisedë',
  // ---- new texts (no prototype equivalent) ----
  setupDone: 'Administratori është krijuar tashmë',
  userNotFound: 'Hyrja nuk u gjet',
  employeeTaken: 'Ky punëtor ka tashmë një hyrje',
  selfDeactivation: 'Nuk mund ta çaktivizosh hyrjen tënde',
  selfRoleChange: 'Nuk mund ta ndryshosh qasjen e hyrjes tënde',
  selfDeletion: 'Nuk mund ta fshish hyrjen tënde',
  whatsappFailed: 'Mesazhi nuk u dërgua',
  notFound: 'Nuk u gjet',
  rateLimited: 'Shumë kërkesa · provo përsëri më vonë',
  badRequest: 'Kërkesa nuk është valide',
  invoiceReaderOff: 'Leximi i faturave me AI nuk është aktivizuar',
  invoiceReadFailed: 'Fatura nuk u lexua me AI',
  internalError: 'Ndodhi një gabim i papritur',
} as const;

/** Activity texts written by the API (type 'auth'); all are new (the prototype had no create or delete flows). */
export const API_ACTIVITY = {
  firstAdmin: 'U krijua administratori i parë',
  userCreated: 'U krijua hyrja',
  userDeleted: 'U fshi hyrja',
} as const;

// ---- health / auth ----------------------------------------------------------------------------

export const integrationModeSchema = z.enum(['mock', 'live']);

export const healthResponseSchema = z.object({
  ok: z.boolean(),
  db: z.enum(['ok', 'error']),
  modes: z.object({ unifi: integrationModeSchema, wa: integrationModeSchema, push: integrationModeSchema }),
  time: timestamptz,
});
export type HealthResponse = z.infer<typeof healthResponseSchema>;

export const authStatusResponseSchema = z.object({ setupRequired: z.boolean() });
export type AuthStatusResponse = z.infer<typeof authStatusResponseSchema>;

export const setupAdminResponseSchema = z.object({ id: uuid });
export type SetupAdminResponse = z.infer<typeof setupAdminResponseSchema>;

// ---- users ------------------------------------------------------------------------------------

/**
 * `PATCH /api/users/:id` — the users-modal fields of `userUpsertForm` (same validators and toast texts),
 * every one optional, plus `active` / `employee_id` for per-employee logins. An empty password means
 * "keep the current password" exactly like the prototype's `saveAuthUser`.
 */
export const userPatchForm = userUpsertForm
  .innerType()
  .pick({ name: true, username: true, password: true })
  .partial()
  .extend({
    active: z.boolean().optional(),
    employee_id: uuid.nullable().optional(),
    /** NEW — the prototype fixed a login's role at creation; turning a staff login into a delivery one needs this. */
    role: role.optional(),
  });
export type UserPatchForm = z.infer<typeof userPatchForm>;
export type UserPatchInput = z.input<typeof userPatchForm>;

export const userResponseSchema = z.object({
  id: uuid,
  name: z.string(),
  username: z.string(),
  role,
  employee_id: uuid.nullable(),
  employee_name: z.string(),
  active: z.boolean(),
  created_at: timestamptz,
  updated_at: timestamptz,
});
export type UserResponse = z.infer<typeof userResponseSchema>;

export const usersListResponseSchema = z.array(userResponseSchema);

// ---- task-assigned webhook --------------------------------------------------------------------

export const HOOK_SECRET_HEADER = 'x-hook-secret';

/** Body posted by the `tasks_notify_assigned` trigger (migration 20260905000300). */
export const taskAssignedHookSchema = z
  .object({
    type: z.enum(['INSERT', 'UPDATE']),
    table: z.literal('tasks'),
    record: z
      .object({
        id: uuid,
        title: z.string(),
        assignee_employee_id: uuid.nullable(),
        priority: taskPriority.optional(),
        due_date: isoDate.optional(),
        due_time: pgTime.nullable().optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type TaskAssignedHook = z.infer<typeof taskAssignedHookSchema>;

export const hookResultSchema = z.object({
  sent: z.number().int().nonnegative(),
  removed: z.number().int().nonnegative(),
  subscriptions: z.number().int().nonnegative(),
});
export type HookResult = z.infer<typeof hookResultSchema>;

/** Notification title shown by the service worker for a new task (task.md §4.8). */
export const TASK_PUSH_TITLE = 'Detyrë e re';

/** A delivery the shop has just handed to this worker. */
export const DELIVERY_PUSH_TITLE = 'Dorëzim i ri';

/** The notification says where to go before the worker opens it: "Dorëzim i ri · nga dyqani". */
export function deliveryPushTitle(location: 'local' | 'warehouse'): string {
  return DELIVERY_PUSH_TITLE + (location === 'local' ? ' · nga dyqani' : ' · nga depoja');
}

export const deliveryAssignedHookSchema = z
  .object({
    type: z.enum(['INSERT', 'UPDATE']),
    table: z.literal('deliveries'),
    record: z
      .object({
        id: uuid,
        sale_id: uuid,
        worker_employee_id: uuid.nullable(),
        worker_name: z.string(),
        customer: z.string(),
        /** The goods to fetch, already rendered by the trigger: "2 × Pleh NPK, 1 × Farë misri". */
        note: z.string(),
        /** NEW — where to fetch them; rows written before the column existed come from the depo. */
        location: z.enum(['local', 'warehouse']).default('warehouse'),
        /** NEW — where the goods go, typed at the till; empty when nobody typed one. */
        address: z.string().default(''),
      })
      .passthrough(),
  })
  .passthrough();
export type DeliveryAssignedHook = z.infer<typeof deliveryAssignedHookSchema>;

// ---- push subscriptions -----------------------------------------------------------------------

export const pushSubscribeResponseSchema = z.object({ id: uuid, endpoint: z.string() });
export type PushSubscribeResponse = z.infer<typeof pushSubscribeResponseSchema>;

export const pushUnsubscribeResponseSchema = z.object({ removed: z.number().int().nonnegative() });
export type PushUnsubscribeResponse = z.infer<typeof pushUnsubscribeResponseSchema>;

// ---- WhatsApp ---------------------------------------------------------------------------------

export const waWebhookResultSchema = z.object({
  received: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  statuses: z.number().int().nonnegative(),
});
export type WaWebhookResult = z.infer<typeof waWebhookResultSchema>;

/**
 * `POST /api/wa/send` — reply to an existing conversation or start one by phone. At least one of
 * `conversationId` / `phone` is required; the API enforces it with `API_MESSAGES.conversationRequired`.
 */
export const waSendMessageRequestSchema = z.object({
  conversationId: uuid.optional(),
  phone: z.string().trim().min(1).optional(),
  body: z.string().trim().min(1, { message: API_MESSAGES.messageRequired }).max(WA_TEXT_MAX),
});
export type WaSendMessageRequest = z.infer<typeof waSendMessageRequestSchema>;

export const waSendMessageResponseSchema = z.object({
  id: uuid,
  conversationId: uuid,
  providerId: z.string().min(1),
  viaTemplate: z.boolean(),
});
export type WaSendMessageResponse = z.infer<typeof waSendMessageResponseSchema>;

/** Meta's customer-service window: free-form text only within 24 h of the last inbound message. */
export const WA_WINDOW_MS = 24 * 60 * 60 * 1000;

// ---- UniFi / presence -------------------------------------------------------------------------

export const unifiClientsResponseSchema = z.object({
  mode: integrationModeSchema,
  count: z.number().int().nonnegative(),
  clients: z.array(unifiClientRecordSchema),
});
export type UnifiClientsResponse = z.infer<typeof unifiClientsResponseSchema>;

/** Prototype `simulateWifiPresence` (line 3018) marks the first ⌈n × SIMULATED_PRESENCE_RATIO⌉ employees online. */
export const SIMULATED_PRESENCE_RATIO = 0.7;

/** `POST /api/dev/simulate-presence` — `employeeIds` overrides the prototype's default slice. */
export const simulatePresenceRequestSchema = z.object({
  employeeIds: z.array(uuid).min(1).optional(),
  at: timestamptz.optional(),
});
export type SimulatePresenceRequest = z.infer<typeof simulatePresenceRequestSchema>;

export const simulatePresenceResponseSchema = z.object({
  date: isoDate,
  time: hhmm,
  clients: z.number().int().nonnegative(),
  present: z.array(
    z.object({
      employeeId: uuid,
      name: z.string(),
      mac: z.string(),
      firstSeen: timestamptz,
      lastSeen: timestamptz,
    }),
  ),
  lastSync: timestamptz,
  state: z.literal(API_MESSAGES.simulationState),
  message: z.literal(API_MESSAGES.simulationDone),
});
export type SimulatePresenceResponse = z.infer<typeof simulatePresenceResponseSchema>;
